import { useState, useEffect, useRef } from 'react';
import { SignedIn, useAuth, useClerk } from '@clerk/clerk-react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../utils/api';
import QRCodeStyling from 'qr-code-styling';
import { resolveMediaUrl } from '../utils/mediaUrl';

/**
 * Validates phone: 10+ digits or E.164 (optional + followed by 10+ digits).
 * @param {string} value
 * @returns {boolean}
 */
function isValidPhone(value) {
  const trimmed = (value || '').trim();
  if (!trimmed) return true; // optional field
  // E.164: optional +, then 10–15 digits
  return /^\+?[0-9]{10,15}$/.test(trimmed.replace(/[\s\-\.]/g, ''));
}

export default function Profile() {
  const { getToken } = useAuth();
  const { signOut } = useClerk();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [vendorId, setVendorId] = useState(null);
  const [stripeAccountId, setStripeAccountId] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [stripeConnecting, setStripeConnecting] = useState(false);
  const [form, setForm] = useState({
    storeName: '',
    locationText: '',
    storeHours: '',
    phone: '',
  });
  const [phoneError, setPhoneError] = useState(null);
  const [saveSuccessShow, setSaveSuccessShow] = useState(false);
  const [saveSuccessFadeOut, setSaveSuccessFadeOut] = useState(false);
  const [coverImageUrl, setCoverImageUrl] = useState(null);
  const [uploadingCover, setUploadingCover] = useState(false);
  const qrRef = useRef(null);
  const qrCodeRef = useRef(null);
  const coverInputRef = useRef(null);

  // Auto-hide save success modal: show 2s, fade out 400ms, then unmount
  useEffect(() => {
    if (!saveSuccessShow) return;
    setSaveSuccessFadeOut(false);
    const t1 = setTimeout(() => setSaveSuccessFadeOut(true), 2000);
    const t2 = setTimeout(() => {
      setSaveSuccessShow(false);
      setSaveSuccessFadeOut(false);
    }, 2400);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [saveSuccessShow]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api('/api/vendors/me', { getToken });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.message || `Error ${res.status}`);
        }
        const data = await res.json();
        if (!cancelled) {
          setVendorId(data.id);
          setStripeAccountId(data.stripeAccountId);
          // Type from DB only (Vendor.IsOwner), not from Clerk
          const owner = data.isOwner ?? false;
          setIsOwner(owner);
          setForm({
            storeName: data.storeName ?? '',
            locationText: data.locationText ?? '',
            storeHours: data.storeHours ?? '',
            phone: data.phone ?? '',
          });
          if (owner) {
            const mRes = await api('/api/markets/owner', { getToken });
            if (mRes.ok) {
              const m = await mRes.json();
              setCoverImageUrl(m.imageUrl || null);
              if (m.name) {
                setForm((prev) => ({ ...prev, storeName: m.name }));
              }
            }
          } else {
            setCoverImageUrl(data.storeImageUrl || null);
          }
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [getToken]);

  // Handle Stripe return query params
  useEffect(() => {
    const stripeParam = searchParams.get('stripe');
    if (stripeParam === 'success') {
      setError(null);
      // Refresh vendor data to get updated stripeAccountId
      (async () => {
        try {
          const res = await api('/api/vendors/me', { getToken });
          if (res.ok) {
            const data = await res.json();
            setStripeAccountId(data.stripeAccountId);
          }
        } catch (err) {
          console.error('Failed to refresh vendor data:', err);
        }
      })();
      // Remove query param
      setSearchParams({}, { replace: true });
    } else if (stripeParam === 'pending') {
      setError('Stripe Connect onboarding is still pending. Please complete the process.');
      setSearchParams({}, { replace: true });
    } else if (stripeParam === 'error') {
      const message = searchParams.get('message');
      setError(`Stripe Connect error: ${message || 'Unknown error occurred'}`);
      setSearchParams({}, { replace: true });
    } else if (stripeParam === 'required') {
      setError('Please complete Stripe Connect to access item management features.');
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, getToken]);

  const handleStripeConnect = async () => {
    setStripeConnecting(true);
    setError(null);
    try {
      const returnUrl = `${window.location.origin}/profile`;
      const res = await api('/api/vendors/create-account-link', {
        getToken,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          returnUrl,
          refreshUrl: returnUrl,
        }),
      });
      
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || data.message || `Error ${res.status}`);
      }
      
      const data = await res.json();
      if (data.url) {
        // Open Stripe Connect onboarding in new window/tab
        window.location.href = data.url;
      } else {
        throw new Error('No account link URL received');
      }
    } catch (err) {
      setError(err.message);
      setStripeConnecting(false);
    }
  };

  // Generate QR code when vendorId is available
  useEffect(() => {
    if (!vendorId || !qrRef.current) return;

    const vendorUrl = `${window.location.origin}/vendor/${vendorId}`;
    
    const qrCode = new QRCodeStyling({
      width: 400,
      height: 400,
      type: 'svg',
      data: vendorUrl,
      dotsOptions: {
        color: '#1e293b',
        type: 'rounded',
      },
      backgroundOptions: {
        color: '#ffffff',
      },
      cornersSquareOptions: {
        color: '#1e293b',
        type: 'extra-rounded',
      },
      cornersDotOptions: {
        color: '#1e293b',
        type: 'dot',
      },
    });

    qrCodeRef.current = qrCode;
    qrCode.append(qrRef.current);

    return () => {
      if (qrRef.current) {
        qrRef.current.innerHTML = '';
      }
    };
  }, [vendorId]);

  const handleDownloadQr = async () => {
    if (!vendorId) return;

    try {
      const vendorUrl = `${window.location.origin}/vendor/${vendorId}`;
      const { downloadQrPng } = await import('../utils/downloadQr');
      await downloadQrPng(vendorUrl, `vendor-${vendorId}-qr.png`);

      // Save ProfileQrUrl to backend (store page link, not image bytes)
      const res = await api('/api/vendors/me', {
        getToken,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          profileQrUrl: vendorUrl,
        }),
      });

      if (!res.ok) {
        console.error('Failed to save QR URL');
      }
    } catch (err) {
      console.error('Error downloading QR code:', err);
      alert('Failed to download QR code');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (name === 'phone') setPhoneError(null);
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCover(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const up = await api('/api/images/upload', {
        getToken,
        method: 'POST',
        body: formData,
      });
      if (!up.ok) {
        const data = await up.json().catch(() => ({}));
        throw new Error(data.error || `Upload failed (${up.status})`);
      }
      const uploaded = await up.json();
      let url = uploaded.url || uploaded.imageUrl;
      if (!url) throw new Error('Upload returned no URL');
      // Prefer relative path for local/dev URLs so they work on LAN
      try {
        const parsed = new URL(url, window.location.origin);
        if (parsed.pathname.startsWith('/public/') || parsed.pathname.startsWith('/uploads/')) {
          url = parsed.pathname;
        }
      } catch {
        // keep original
      }

      if (isOwner) {
        const res = await api('/api/markets/owner', {
          getToken,
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ imageUrl: url }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || data.details || `Save failed (${res.status})`);
        }
      } else {
        const res = await api('/api/vendors/me', {
          getToken,
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ storeImageUrl: url }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.message || data.error || data.details || `Save failed (${res.status})`);
        }
      }
      setCoverImageUrl(url);
      setSaveSuccessShow(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploadingCover(false);
      if (coverInputRef.current) coverInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const phoneTrimmed = form.phone.trim();
    if (phoneTrimmed && !isValidPhone(form.phone)) {
      setPhoneError('Enter 10+ digits or E.164 (e.g. +1234567890)');
      return;
    }
    setPhoneError(null);
    setSaving(true);
    setError(null);
    try {
      const res = await api('/api/vendors/me', {
        getToken,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          storeName: form.storeName || null,
          locationText: form.locationText || null,
          storeHours: form.storeHours || null,
          phone: phoneTrimmed || null,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || `Error ${res.status}`);
      }
      setSaveSuccessShow(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-2xl text-slate-500">Loading profile…</p>
      </div>
    );
  }

  const hasStripeAccount = stripeAccountId != null && stripeAccountId.trim() !== '';

  return (
    <SignedIn>
      {/* Save success modal — centered, theme-matched, fades away */}
      {saveSuccessShow && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity duration-300 ${
            saveSuccessFadeOut ? 'opacity-0' : 'opacity-100'
          }`}
          aria-live="polite"
          aria-label="Profile saved"
        >
          <div
            className="bg-black/40 absolute inset-0"
            aria-hidden
          />
          <div
            className="relative animate-fade-slide-in rounded-2xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-8 py-6 shadow-xl flex flex-col items-center gap-3 min-w-[200px]"
            role="alert"
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300">
              <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </span>
            <span className="text-xl font-semibold text-slate-800 dark:text-slate-100">Saved</span>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-6">
        <header className="flex items-center justify-between gap-4">
          <h1 className="text-3xl font-semibold">Store Profile</h1>
          <button
            type="button"
            onClick={() => signOut({ redirectUrl: '/' })}
            className="shrink-0 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 underline underline-offset-2"
          >
            Sign out
          </button>
        </header>

        {/* Stripe Connect Section */}
        {!hasStripeAccount && (
          <div className="rounded-xl border-2 border-amber-400 dark:border-amber-500 bg-amber-50 dark:bg-amber-900/20 p-6 flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <h2 className="text-2xl font-semibold text-amber-900 dark:text-amber-100">
                Complete Stripe Connect
              </h2>
              <p className="text-lg text-amber-800 dark:text-amber-200">
                You need to complete Stripe Connect onboarding to list items and manage your store. This allows you to receive payments securely.
              </p>
            </div>
            <button
              type="button"
              onClick={handleStripeConnect}
              disabled={stripeConnecting}
              className="h-16 text-2xl w-full rounded-xl font-semibold bg-amber-600 text-white hover:bg-amber-700 active:bg-amber-800 disabled:opacity-50 transition-colors"
            >
              {stripeConnecting ? 'Connecting…' : 'Complete Stripe Connect'}
            </button>
          </div>
        )}

        {error && (
          <p className="rounded-xl bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 p-4 text-lg">
            {error}
          </p>
        )}

        <div className="flex flex-col gap-3 rounded-xl border border-slate-200 dark:border-slate-700 p-4 bg-white dark:bg-slate-800">
          <span className="text-lg font-medium">
            {isOwner ? 'Flea market tab image' : 'Vendor tab image'}
          </span>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            {isOwner
              ? 'Shown on the home markets list and market page.'
              : 'Shown on your public vendor storefront and the market vendor list.'}
          </p>
          {coverImageUrl && (
            <div className="aspect-[21/9] w-full max-w-md rounded-lg overflow-hidden bg-slate-100">
              <img
                src={resolveMediaUrl(coverImageUrl) || coverImageUrl}
                alt="Cover"
                className="w-full h-full object-cover"
              />
            </div>
          )}
          <input
            ref={coverInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleCoverUpload}
          />
          <button
            type="button"
            disabled={uploadingCover}
            onClick={() => coverInputRef.current?.click()}
            className="h-12 px-4 rounded-xl font-semibold bg-slate-800 text-white hover:bg-slate-900 disabled:opacity-50 self-start"
          >
            {uploadingCover ? 'Uploading…' : coverImageUrl ? 'Replace image' : 'Upload image'}
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-6">
          <label className="flex flex-col gap-2">
            <span className="text-lg font-medium">{isOwner ? 'Flea market name' : 'Store name'}</span>
            <input
              type="text"
              name="storeName"
              value={form.storeName}
              onChange={handleChange}
              className="h-16 text-2xl w-full rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-4"
              placeholder={isOwner ? 'Flea market name' : 'Store name'}
            />
          </label>
          <label className="flex flex-col gap-2">
            <span className="text-lg font-medium">Location</span>
            <input
              type="text"
              name="locationText"
              value={form.locationText}
              onChange={handleChange}
              className="h-16 text-2xl w-full rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-4"
              placeholder="Location"
            />
          </label>
          <label className="flex flex-col gap-2">
            <span className="text-lg font-medium">Store hours</span>
            <input
              type="text"
              name="storeHours"
              value={form.storeHours}
              onChange={handleChange}
              className="h-16 text-2xl w-full rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-4"
              placeholder="e.g. Sat–Sun 9am–5pm"
            />
          </label>
          <label className="flex flex-col gap-2">
            <span className="text-lg font-medium">Phone</span>
            <input
              type="tel"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              className="h-16 text-2xl w-full rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-4"
              placeholder="+1 234 567 8900"
            />
            {phoneError && (
              <p className="text-red-600 dark:text-red-400 text-base">{phoneError}</p>
            )}
          </label>
          <button
            type="submit"
            disabled={saving}
            className="h-16 text-2xl w-full rounded-xl font-semibold bg-indigo-600 text-white hover:bg-indigo-700 active:bg-indigo-800 disabled:opacity-50 transition-colors"
          >
            {saving ? 'Saving…' : 'Save profile'}
          </button>
        </form>
        {vendorId && (
          <div className="flex flex-col gap-4 items-center">
            <h2 className="text-2xl font-semibold">Profile QR Code</h2>
            <div ref={qrRef} className="flex justify-center bg-white p-4 rounded-xl"></div>
            <button
              type="button"
              onClick={handleDownloadQr}
              className="h-16 text-2xl w-full rounded-xl font-semibold bg-slate-600 text-white hover:bg-slate-700 active:bg-slate-800 transition-colors"
            >
              Download QR Code
            </button>
          </div>
        )}

      </div>
    </SignedIn>
  );
}
